import { ClientContext } from "ClientProvider";
import { getTransactionByGateway } from "api/graphQL_dashboard";
import { useEffect, useState, useContext, useRef, useCallback } from "react";

/* ─── helpers ─────────────────────────────────────────────── */
function calcEntries(obj: Record<string, number>) {
  const total = Object.values(obj).reduce((s, v) => s + v, 0);
  if (total === 0) return null;
  return Object.entries(obj)
    .filter(([, v]) => v !== 0)
    .map(([key, value]) => ({ key, original: value }))
    .sort((a, b) => b.original - a.original);
}
function lighten(hex: string, a: number) {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.min(255, ((n >> 16) & 0xff) + Math.round(a * 255));
  const g = Math.min(255, ((n >> 8) & 0xff) + Math.round(a * 255));
  const b = Math.min(255, (n & 0xff) + Math.round(a * 255));
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
}
function darken(hex: string, a: number) {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, ((n >> 16) & 0xff) - Math.round(a * 255));
  const g = Math.max(0, ((n >> 8) & 0xff) - Math.round(a * 255));
  const b = Math.max(0, (n & 0xff) - Math.round(a * 255));
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
}
function generateShades(base: string, count: number) {
  return Array.from({ length: count }, (_, i) => darken(base, i / (count * 1.2)));
}

const APPROVE_COLORS = generateShades('#00e5a8', 40);
const DECLINE_COLORS = generateShades('#ff4d6d', 40);
const ERRORED_COLORS = generateShades('#ff9f1c', 40);
const FAILED_COLORS = generateShades('#8b5cf6', 40);

/* ─── canvas size helper — reads screen width once ── */
function getCanvasSize(): number {
  const w = window.innerWidth;
  if (w <= 360) return 150;
  if (w <= 480) return 170;
  if (w <= 768) return 190;
  return 220;
}

/* ─── 3D canvas donut hook ──────────────────────────────── */
function use3DDonut(
  canvasRef: React.RefObject<HTMLCanvasElement>,
  entries: { key: string; original: number }[] | null,
  colors: string[],
  isApprove: boolean,
  isFailed: boolean,
) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !entries || entries.length === 0) return;
    const ctx = canvas.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;

    // Dynamic size based on viewport
    const S = getCanvasSize();
    canvas.width = S * dpr;
    canvas.height = S * dpr;
    canvas.style.width = S + 'px';
    canvas.style.height = S + 'px';
    ctx.scale(dpr, dpr);

    const total = entries.reduce((s, e) => s + e.original, 0);
    const cx = S / 2, cy = S / 2;

    // Scale ring proportionally to canvas size
    const scale = S / 220;
    const OR = Math.round(90 * scale);
    const IR = Math.round(52 * scale);
    const GAP = 0.045;
    const DUR = 1300;
    const start = Date.now();
    let hovered = -1, raf: number;

    function getSegs(prog: number) {
      let a = -Math.PI / 2;
      return entries!.map((e, i) => {
        const full = (e.original / total) * 2 * Math.PI;
        const dg = Math.min(GAP, full * 0.25);
        const sweep = Math.max((full - dg * 2) * Math.min(prog, 1), 0);
        const sa = a + dg, ea = sa + sweep;
        a += full;
        return { sa, ea, key: e.key, val: e.original, color: colors[i % colors.length] };
      });
    }

    function draw(prog: number) {
      ctx.clearRect(0, 0, S, S);
      const ss = getSegs(prog);

      // Pass 1: extrusion depth layers
      ss.forEach(({ sa, ea, color }, i) => {
        if (ea <= sa) return;
        const isH = i === hovered;
        const oR = isH ? OR + Math.round(8 * scale) : OR;
        const iR = isH ? IR - Math.round(4 * scale) : IR;
        const EX = isH ? Math.round(7 * scale) : Math.round(5 * scale);
        const mid = (sa + ea) / 2;
        const px = isH ? Math.cos(mid) * Math.round(6 * scale) : 0;
        const py = isH ? Math.sin(mid) * Math.round(6 * scale) : 0;
        for (let d = EX; d >= 1; d--) {
          ctx.save(); ctx.translate(px, py + d);
          ctx.beginPath(); ctx.arc(cx, cy, oR, sa, ea); ctx.arc(cx, cy, iR, ea, sa, true); ctx.closePath();
          ctx.fillStyle = darken(color, 0.55);
          ctx.globalAlpha = Math.min(0.05 + (EX - d) * 0.045, 0.3);
          ctx.fill(); ctx.restore();
        }
      });

      // Pass 2: main faces + specular highlights
      ss.forEach(({ sa, ea, color }, i) => {
        if (ea <= sa) return;
        const isH = i === hovered;
        const oR = isH ? OR + Math.round(8 * scale) : OR;
        const iR = isH ? IR - Math.round(4 * scale) : IR;
        const mid = (sa + ea) / 2;
        const px = isH ? Math.cos(mid) * Math.round(6 * scale) : 0;
        const py = isH ? Math.sin(mid) * Math.round(6 * scale) : 0;
        ctx.save(); ctx.translate(px, py);

        ctx.beginPath(); ctx.arc(cx, cy, oR, sa, ea); ctx.arc(cx, cy, iR, ea, sa, true); ctx.closePath();
        const rg = ctx.createRadialGradient(cx, cy, iR * 0.55, cx, cy, oR);
        rg.addColorStop(0, lighten(color, 0.32));
        rg.addColorStop(0.42, color);
        rg.addColorStop(1, darken(color, 0.25));
        ctx.fillStyle = rg; ctx.globalAlpha = 1; ctx.fill();

        ctx.beginPath(); ctx.arc(cx, cy, oR, sa, ea); ctx.arc(cx, cy, iR + (OR - IR) * 0.48, ea, sa, true); ctx.closePath();
        const sg = ctx.createRadialGradient(cx, cy - (OR - IR) * 0.45, 0, cx, cy, oR);
        sg.addColorStop(0, 'rgba(255,255,255,.6)');
        sg.addColorStop(0.55, 'rgba(255,255,255,.12)');
        sg.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = sg; ctx.fill();

        ctx.beginPath(); ctx.arc(cx, cy, oR, sa, ea); ctx.arc(cx, cy, oR - 3.5, ea, sa, true); ctx.closePath();
        ctx.fillStyle = lighten(color, 0.28); ctx.globalAlpha = 0.42; ctx.fill();

        ctx.beginPath(); ctx.arc(cx, cy, iR + 2, sa, ea); ctx.arc(cx, cy, iR, ea, sa, true); ctx.closePath();
        ctx.fillStyle = 'rgba(255,255,255,.35)'; ctx.globalAlpha = 0.6; ctx.fill();

        ctx.restore();
      });

      // Center hole
      const hg = ctx.createRadialGradient(cx, cy, IR - Math.round(10 * scale), cx, cy, IR + Math.round(6 * scale));
      hg.addColorStop(0, 'rgba(0,0,0,0)'); hg.addColorStop(0.65, 'rgba(0,0,0,.05)'); hg.addColorStop(1, 'rgba(0,0,0,.16)');
      ctx.beginPath(); ctx.arc(cx, cy, IR + Math.round(6 * scale), 0, Math.PI * 2); ctx.fillStyle = hg; ctx.globalAlpha = 1; ctx.fill();
      ctx.beginPath(); ctx.arc(cx, cy, IR - 1, 0, Math.PI * 2);
      ctx.fillStyle = isApprove ? 'rgba(235,255,248,.97)' : isFailed ? 'rgba(255,249,232,.97)' : 'rgba(255,242,242,.97)'; ctx.fill();
      ctx.strokeStyle = isApprove ? 'rgba(0,180,130,.2)' : isFailed ? 'rgba(230,145,0,.25)' : 'rgba(200,30,50,.2)'; ctx.lineWidth = 2; ctx.stroke();
    }

    function animate() {
      const raw = Math.min((Date.now() - start) / DUR, 1);
      const ease = raw < 0.5 ? 4 * raw * raw * raw : 0.5 * Math.pow(2 * raw - 2, 3) + 1;
      draw(ease);
      if (raw < 1) raf = requestAnimationFrame(animate);
    }
    raf = requestAnimationFrame(animate);

    function onMove(e: MouseEvent) {
      const rect = canvas.getBoundingClientRect();
      const mx = (e.clientX - rect.left) * (S / rect.width);
      const my = (e.clientY - rect.top) * (S / rect.height);
      const dx = mx - cx, dy = my - cy, dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < IR - 2 || dist > OR + 12) {
        if (hovered !== -1) { hovered = -1; draw(1); }
        return;
      }
      let a = Math.atan2(dy, dx);
      if (a < -Math.PI / 2) a += 2 * Math.PI;
      const ss = getSegs(1); let found = -1;
      ss.forEach(({ sa, ea }, i) => { let s = sa, en = ea; if (s > en) en += 2 * Math.PI; if (a >= s && a <= en) found = i; });
      if (found !== hovered) { hovered = found; draw(1); }
    }
    function onLeave() { hovered = -1; draw(1); }
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    return () => { cancelAnimationFrame(raf); canvas.removeEventListener('mousemove', onMove); canvas.removeEventListener('mouseleave', onLeave); };
  }, [entries, colors, isApprove, isFailed]);
}

/* ─── component ─────────────────────────────────────────── */
const TransactionPieChartCard = ({
  type, datesTop, timeFrameTop, selectOrg, selectClient, selectCard,
}: {
  type?: string; datesTop?: any; timeFrameTop?: any;
  selectOrg?: any; selectClient?: any; selectCard?: any;
}) => {
  const [entries, setEntries] = useState<ReturnType<typeof calcEntries>>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { dashboardData } = useContext(ClientContext);
  const [timeFrame, setTimeFrame] = useState<string>(timeFrameTop);
  const [dates, setDates] = useState<[Date, Date]>(datesTop);
  const isDecline = type === 'decline';
  const isErrored = type === 'errored';
  const isFailed = type === 'failed';
  const COLORS = isDecline
    ? DECLINE_COLORS
    : isErrored
      ? ERRORED_COLORS
      : isFailed
        ? FAILED_COLORS
        : APPROVE_COLORS;

  const canvasRef = useRef<HTMLCanvasElement>(null);

  const fetchData = useCallback(() => {
    setIsLoading(true);
    let data: any = {
      time_frame: timeFrame,
      org_ids: selectOrg?.length > 0 ? selectOrg.map((m: any) => m?.id).toString() : '',
      client_ids: selectClient?.length > 0 ? selectClient.map((m: any) => m?.id).toString() : '',
      card_type: selectCard?.length > 0 ? selectCard.map((m: any) => m?.value).toString() : '',
    };
    if (dates) { data.start_date = dates[0].toISOString().substring(0, 10); data.end_date = dates[1].toISOString().substring(0, 10); }
    if (timeFrame !== 'custom') { delete data.start_date; delete data.end_date; }
    getTransactionByGateway(type, dashboardData, data)
      .then((d: Record<string, number>) => setEntries(calcEntries(d)))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [dashboardData, dates, timeFrame, selectOrg, selectClient, selectCard]);

  useEffect(() => { const t = setTimeout(fetchData, 1000); return () => clearTimeout(t); }, [fetchData]);
  useEffect(() => { setTimeFrame(timeFrameTop); setDates(datesTop); }, [timeFrameTop, datesTop]);

  use3DDonut(canvasRef, entries, COLORS, !isDecline && !isFailed, isFailed);
  const total = entries?.reduce((s, e) => s + e.original, 0) ?? 0;

return (
    <div className={`tpc-card ${isDecline ? 'tpc-card--red' : isErrored ? 'tpc-card--errored' : isFailed ? 'tpc-card--failed' : 'tpc-card--green'}`}>
      <div className="tpc-orb" />
      <div className="tpc-shine" />

      <div className="tpc-hdr">
        <div className="tpc-hdr-left">
          <div className={`tpc-icon ${isDecline ? 'tpc-icon--red' : isErrored ? 'tpc-icon--errored' : isFailed ? 'tpc-icon--failed' : 'tpc-icon--green'}`}>
            {isDecline ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            ) : isErrored ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><path d="M12 7v6"/><circle cx="12" cy="16.5" r="1" fill="#fff" stroke="none"/>
              </svg>
            ) : isFailed ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.3 2.7h3.4L16 6l3.3 2.3v3.4L22 15l-2.7 2.3V20l-3.3 1.3L13.7 24h-3.4L8 21.3 4.7 20v-2.7L2 15l2.7-3.3V8.3L8 6l2.3-3.3z"/>
                <path d="M12 8v5"/><circle cx="12" cy="16.5" r="1" fill="#fff" stroke="none"/>
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
              </svg>
            )}
          </div>
          <div>
            <h3 className="tpc-title">{isDecline ? 'Declined' : isErrored ? 'Errored' : isFailed ? 'Failed' : 'Approved'} Transactions</h3>
            <p className={`tpc-subtitle ${isDecline ? 'tpc-subtitle--red' : isErrored ? 'tpc-subtitle--errored' : isFailed ? 'tpc-subtitle--failed' : 'tpc-subtitle--green'}`}>By payment gateway</p>
          </div>
        </div>
        {!isLoading && total > 0 && (
          <div className={`tpc-badge ${isDecline ? 'tpc-badge--red' : isErrored ? 'tpc-badge--errored' : isFailed ? 'tpc-badge--failed' : 'tpc-badge--green'}`}>
            {total.toLocaleString()} total
          </div>
        )}
      </div>

      <div className="tpc-body">
        {isLoading ? (
          <div className="tpc-state">
            <div className={`tpc-spinner ${isDecline ? 'tpc-spinner--red' : isErrored ? 'tpc-spinner--errored' : isFailed ? 'tpc-spinner--failed' : 'tpc-spinner--green'}`} />
            <p className="tpc-hint">Fetching data…</p>
          </div>
        ) : entries && entries.length > 0 ? (
          <>
            <div className="tpc-chart-col">
              <div className="tpc-canvas-wrap">
                <canvas ref={canvasRef} style={{ cursor: 'crosshair', display: 'block' }} />
              </div>
            </div>
            <div className="tpc-leg-col">
              {entries.map((e, i) => {
                const pct = total > 0 ? Math.round((e.original / total) * 100) : 0;
                const color = COLORS[i % COLORS.length];
                return (
                  <div key={i} className={`tpc-leg-row ${isDecline ? 'tpc-leg-row--red' : isErrored ? 'tpc-leg-row--errored' : isFailed ? 'tpc-leg-row--failed' : 'tpc-leg-row--green'}`}>
                    <div className="tpc-swatch" style={{ background: color, boxShadow: `0 2px 6px ${color}55` }} />
                    <span className="tpc-leg-name">{e.key}</span>
                    <span className="tpc-leg-val">{e.original.toLocaleString()}</span>
                    <div className="tpc-bar-bg" style={{ background: lighten(color, .38) }}>
                      <div className="tpc-bar-fill" style={{ width: `${pct}%`, background: color }} />
                    </div>
                    <span className="tpc-leg-pct">{pct}%</span>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="tpc-state">
            <div className="tpc-empty-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/>
              </svg>
            </div>
            <p className="tpc-empty-title">No data available</p>
            <p className="tpc-hint">No {isDecline ? 'declined' : isErrored ? 'errored' : isFailed ? 'failed' : 'approved'} transactions for selected filters</p>
          </div>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');

        .tpc-card { font-family:'Plus Jakarta Sans',sans-serif; border-radius:28px; overflow:hidden; display:flex; flex-direction:column; height:100%; position:relative; }
        .tpc-card--green { background:linear-gradient(160deg,#f0fff8 0%,#e6fff4 50%,#f5fffb 100%); border:1.5px solid #b2f5db; }
        .tpc-card--red   { background:linear-gradient(160deg,#fff5f5 0%,#ffe8e8 50%,#fff8f8 100%); border:1.5px solid #ffc5c5; }
        .tpc-card--errored { background:linear-gradient(160deg,#fffaf0 0%,#fff1d6 50%,#fffaf2 100%); border:1.5px solid #ffd98f; }
        .tpc-card--failed { background:linear-gradient(160deg,#f8f5ff 0%,#eee8ff 50%,#faf8ff 100%); border:1.5px solid #d9ccff; }

        .tpc-orb { position:absolute; top:-50px; right:-50px; width:160px; height:160px; border-radius:50%; pointer-events:none; opacity:.09; }
        .tpc-card--green .tpc-orb { background:radial-gradient(circle,#00e5a8,transparent); }
        .tpc-card--red   .tpc-orb { background:radial-gradient(circle,#ff6b7a,transparent); }
        .tpc-card--errored .tpc-orb { background:radial-gradient(circle,#ffb020,transparent); }
        .tpc-card--failed .tpc-orb { background:radial-gradient(circle,#8b5cf6,transparent); }

        .tpc-shine { position:absolute; inset:0; background:linear-gradient(120deg,transparent 30%,rgba(255,255,255,.45) 50%,transparent 70%); animation:tpcShine 4s ease-in-out infinite; pointer-events:none; border-radius:28px; z-index:0; }
        @keyframes tpcShine { 0%,100%{opacity:0;transform:translateX(-100%)} 50%{opacity:1;transform:translateX(100%)} }

        .tpc-hdr { padding:16px 18px 12px; display:flex; align-items:center; justify-content:space-between; gap:8px; flex-wrap:wrap; border-bottom:1px solid rgba(0,0,0,.05); position:relative; z-index:2; }
        .tpc-hdr-left { display:flex; align-items:center; gap:10px; }
        .tpc-icon { width:42px; height:42px; border-radius:14px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .tpc-icon--green { background:linear-gradient(135deg,#00e5a8,#00a67c); box-shadow:0 6px 20px rgba(0,198,150,.45),inset 0 1.5px 0 rgba(255,255,255,.5); }
        .tpc-icon--red   { background:linear-gradient(135deg,#ff7a87,#d63448); box-shadow:0 6px 20px rgba(214,52,72,.45),inset 0 1.5px 0 rgba(255,255,255,.5); }
        .tpc-icon--errored { background:linear-gradient(135deg,#ffb52e,#e58a00); box-shadow:0 6px 20px rgba(229,138,0,.4),inset 0 1.5px 0 rgba(255,255,255,.5); }
        .tpc-icon--failed { background:linear-gradient(135deg,#a78bfa,#6d28d9); box-shadow:0 6px 20px rgba(109,40,217,.4),inset 0 1.5px 0 rgba(255,255,255,.5); }
        .tpc-icon svg { filter:drop-shadow(0 1px 4px rgba(0,0,0,.25)); }
        .tpc-title { font-size:13px; font-weight:800; color:#0d2137; letter-spacing:-.2px; line-height:1.3; margin-bottom:2px; }
        .tpc-subtitle { font-size:9.5px; font-weight:700; letter-spacing:.6px; text-transform:uppercase; }
        .tpc-subtitle--green { color:#00a67c; }
        .tpc-subtitle--red   { color:#d63448; }
        .tpc-subtitle--errored { color:#c87500; }
        .tpc-subtitle--failed { color:#6d28d9; }
        .tpc-badge { font-size:11px; font-weight:900; padding:5px 13px; border-radius:100px; white-space:nowrap; }
        .tpc-badge--green { background:linear-gradient(135deg,#00e5a8,#00b889); color:#003d2a; box-shadow:0 4px 12px rgba(0,198,150,.35); }
        .tpc-badge--red   { background:linear-gradient(135deg,#ff7a87,#e04055); color:#fff; box-shadow:0 4px 12px rgba(214,52,72,.35); }
        .tpc-badge--errored { background:linear-gradient(135deg,#ffb52e,#e58a00); color:#5a3200; box-shadow:0 4px 12px rgba(229,138,0,.3); }
        .tpc-badge--failed { background:linear-gradient(135deg,#a78bfa,#7c3aed); color:#fff; box-shadow:0 4px 12px rgba(124,58,237,.3); }

        /* ── Desktop body: side by side ── */
        .tpc-body { display:grid; grid-template-columns:240px 1fr; gap:0; padding:10px 0 20px; align-items:center; position:relative; z-index:2; }

        .tpc-chart-col { display:flex; align-items:center; justify-content:center; padding:10px 0 10px 14px; min-height:220px; }
        .tpc-canvas-wrap { position:relative; width:220px; height:220px; margin:auto; flex-shrink:0; }
        .tpc-canvas-wrap canvas { display:block; }
        .tpc-center { position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; pointer-events:none; }
        .tpc-center-num { font-size:34px; font-weight:900; letter-spacing:-1.5px; line-height:1; }
        .tpc-center-num--green { color:#006b4f; }
        .tpc-center-num--red   { color:#b01020; }
        .tpc-center-num--errored { color:#a85f00; }
        .tpc-center-num--failed { color:#6d28d9; }
        .tpc-center-lbl { font-size:8.5px; font-weight:800; letter-spacing:.8px; text-transform:uppercase; margin-top:4px; opacity:.45; color:#0d2137; }

        .tpc-leg-col { padding:12px 16px 4px 8px; display:flex; flex-direction:column; gap:6px; max-height:240px; overflow-y:auto; justify-content:flex-start; }
        .tpc-leg-col::-webkit-scrollbar { width:3px; }
        .tpc-leg-col::-webkit-scrollbar-thumb { border-radius:3px; background:rgba(0,0,0,.18); }

        .tpc-leg-row { display:flex; align-items:center; gap:8px; padding:6px 10px; border-radius:11px; cursor:default; transition:all .15s; border-left:3px solid transparent; }
        .tpc-leg-row--green:hover { background:rgba(0,200,150,.09); border-left-color:#00c896; transform:translateX(3px); }
        .tpc-leg-row--red:hover   { background:rgba(255,77,109,.09); border-left-color:#ff4d6d; transform:translateX(3px); }
        .tpc-leg-row--errored:hover { background:rgba(255,159,28,.10); border-left-color:#ff9f1c; transform:translateX(3px); }
        .tpc-leg-row--failed:hover { background:rgba(124,58,237,.09); border-left-color:#7c3aed; transform:translateX(3px); }
        .tpc-swatch { width:10px; height:10px; border-radius:4px; flex-shrink:0; }
        .tpc-leg-name { font-size:11px; color:#2d3f5a; font-weight:700; flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .tpc-leg-val  { font-size:12px; font-weight:900; color:#0d2137; min-width:28px; text-align:right; }
        .tpc-bar-bg   { width:60px; height:5px; border-radius:100px; overflow:hidden; flex-shrink:0; }
        .tpc-bar-fill { height:100%; border-radius:100px; transition:width .7s cubic-bezier(.4,0,.2,1); }
        .tpc-leg-pct  { font-size:10px; font-weight:800; min-width:26px; text-align:right; color:#5a6a8a; }

        .tpc-state { display:flex; flex-direction:column; align-items:center; justify-content:center; gap:10px; min-height:200px; padding:32px 24px; grid-column:1/-1; }
        .tpc-empty-icon { width:50px; height:50px; border-radius:14px; background:rgba(255,255,255,.8); border:1.5px solid rgba(0,0,0,.06); display:flex; align-items:center; justify-content:center; color:#b0bae8; }
        .tpc-empty-title { font-size:13px; font-weight:800; color:#475080; }
        .tpc-hint { font-size:11.5px; color:#8a96b8; font-weight:500; text-align:center; max-width:190px; line-height:1.5; }
        .tpc-spinner { width:28px; height:28px; border-radius:50%; border:2.5px solid transparent; animation:tpcSpin .7s linear infinite; }
        .tpc-spinner--green { border-color:#b2f5db; border-top-color:#00a67c; }
        .tpc-spinner--red   { border-color:#ffc5c5; border-top-color:#d63448; }
        .tpc-spinner--errored { border-color:#ffd98f; border-top-color:#e58a00; }
        .tpc-spinner--failed { border-color:#d9ccff; border-top-color:#7c3aed; }
        @keyframes tpcSpin { to { transform:rotate(360deg); } }

        /* ══ RESPONSIVE ══ */

        /* Large desktop (≥1400px) */
        @media (min-width:1400px) {
          .tpc-body { grid-template-columns:260px 1fr; }
          .tpc-canvas-wrap { width:240px; height:240px; }
          .tpc-center-num { font-size:38px; }
        }

        /* Tablet landscape (≤1024px) */
        @media (max-width:1024px) {
          .tpc-body { grid-template-columns:210px 1fr; }
          .tpc-canvas-wrap { width:200px; height:200px; }
          .tpc-center-num { font-size:30px; }
          .tpc-leg-col { max-height:210px; }
          .tpc-bar-bg { width:44px; }
        }

        /* Tablet portrait (≤768px) — stack chart above legend */
        @media (max-width:768px) {
          .tpc-card { border-radius:22px; }
          .tpc-body { grid-template-columns:1fr; padding:8px 0 16px; }
          .tpc-chart-col { padding:14px 0 6px; min-height:unset; }
          .tpc-canvas-wrap { width:190px; height:190px; }
          .tpc-center-num { font-size:28px; }
          .tpc-leg-col { padding:4px 16px 12px; max-height:200px; }
          .tpc-bar-bg { width:48px; }
        }

        /* Mobile (≤480px) — compact stacked layout */
        @media (max-width:480px) {
          .tpc-card { border-radius:18px; }
          .tpc-hdr { padding:12px 14px 10px; gap:6px; }
          .tpc-icon { width:36px; height:36px; border-radius:11px; }
          .tpc-icon svg { width:15px; height:15px; }
          .tpc-title { font-size:12px; }
          .tpc-badge { font-size:10px; padding:4px 10px; }

          /* Chart centered, compact */
          .tpc-body { padding:6px 0 14px; }
          .tpc-chart-col { padding:10px 0 4px; }
          .tpc-canvas-wrap { width:170px; height:170px; }
          .tpc-center-num { font-size:24px; letter-spacing:-1px; }
          .tpc-center-lbl { font-size:7.5px; }

          /* Legend more compact */
          .tpc-leg-col {
            padding:2px 12px 12px;
            gap:3px;
            /* No fixed max-height — let it show all items naturally */
            max-height:none;
            overflow-y:visible;
          }
          .tpc-leg-row { padding:5px 8px; gap:6px; }
          .tpc-leg-name { font-size:10.5px; }
          .tpc-leg-val  { font-size:11px; min-width:24px; }
          .tpc-leg-pct  { font-size:9.5px; min-width:22px; }
          .tpc-bar-bg   { width:32px; height:4px; }
          .tpc-swatch   { width:8px; height:8px; }
        }

        /* Very small (≤360px) */
        @media (max-width:360px) {
          .tpc-canvas-wrap { width:150px; height:150px; }
          .tpc-center-num { font-size:21px; }
          .tpc-leg-row { gap:5px; padding:4px 6px; }
          .tpc-bar-bg { width:24px; }
          .tpc-leg-val { min-width:20px; }
        }

        @media (max-width: 380px) {
          .tpc-chart-col { display:none  }
        }
      `}</style>
    </div>
  );
};

export default TransactionPieChartCard;